import React from "react";
import { toast } from "react-toastify";

import * as Sentry from "@sentry/react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardTitle,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
  UncontrolledTooltip,
} from "reactstrap";
import LanguageRegionForm from "../components/Forms/LanguageRegionForm";
import NotifierForm from "../components/Forms/NotifierForm";
import StoreConfigForm from "../components/Forms/StoreConfigForm";
import VideoTuneForm from "../components/Forms/VideoTuneForm";
import ServerServicesList from "../components/ServersServices";
import DisplayWhen from "../components/common/base/DisplayWhen";
import IncidentList from "../components/incidents/IncidentList";
import InstallationWizard from "../components/installations/InstallationWizard";
import OpeningHoursList from "../components/openinghours/OpeningHoursList";
import {
  adminEditorRole,
  csmMaintenanceRole,
  csmRole,
  configViewerRoles,
  dashcamRoles,
  editorRole,
  qualityRole,
} from "../components/shared/constants";
import userHasRole, { userHasAnyRole } from "../components/shared/utils/authUtils";
import setDocumentTitle from "../components/shared/utils/documentTitle";
import TreePrototype from "../components/cameraPrototypes/TreePrototype";
import { getStoreTree } from "../components/cameraPrototypes/shared/captureDeviceService";
import {
  computeStreamKpisFromCameraTree,
  computeStreamKpisFromLegacyStore,
} from "../components/cameraPrototypes/shared/streamKpis";
import VtIpList from "../components/vtip/VtIpList";
import ConfigViewerModal from "../components/common/ConfigViewerModal";
import { getStoreContainersStatus } from "../services/machineOperationsService";
import {
  getStoreConfig,
  refreshStoreFromCrm,
  forceActiveStore,
  removeForceActiveStore,
} from "../services/configCenterService";
import { getStore, getStorePaymentStatus } from "../services/storeService";
import { ActionButton, LinkButton } from "../components/common/buttons";

class StoreDetailView extends React.Component {
  state = {
    store: null,
    paymentStatus: null,
    newStore: false,
    deleteMode: false,
    restartMode: false,
    servers: {},
    activeTabIndex: 0,
    isConfigModalOpen: false,
    isSyncingFromCrm: false,
    isTogglingForceActive: false,
  };

  // Identifies the in-flight store load; see isStaleFetch.
  storeFetchEpoch = 0;

  async componentDidMount() {
    const { match, location } = this.props;
    const storeId = match.params.id;
    if (location) {
      const tabParam = new URLSearchParams(location.search).get("tab");
      const tabIndex = Number(tabParam);
      if (tabParam !== null && Number.isInteger(tabIndex) && tabIndex >= 0) {
        // The legacy Video Recorders tab (1) is gone; its bookmarks land on Cameras (9).
        this.setState({ activeTabIndex: tabIndex === 1 ? 9 : tabIndex });
      }
    }
    if (storeId === "new") {
      const newStore = true;
      this.setState({ newStore });
      return;
    }
    await this.fetchStore(storeId);
  }

  async componentDidUpdate(prevProps) {
    const prevStoreId = prevProps.match.params.id;
    const { match } = this.props;
    const storeId = match.params.id;
    if (prevStoreId !== storeId) {
      await this.fetchStore(storeId);
    }
  }

  handleSimulationChange = (inSimulation) => {
    this.setState((prevState) => {
      const { store } = prevState;
      if (!store) return null;
      return { store: { ...store, in_simulation: !!inSimulation } };
    });
  };

  isCreationRole = () => userHasRole(editorRole) && !userHasRole(qualityRole);

  toggleDeleteMode = () => {
    const { deleteMode } = this.state;
    this.setState({ deleteMode: !deleteMode });
  };

  toggleRestartMode = () => {
    const { restartMode } = this.state;
    this.setState({ restartMode: !restartMode });
  };

  toggleConfigModal = () => {
    this.setState((prevState) => ({ isConfigModalOpen: !prevState.isConfigModalOpen }));
  };

  handleFetchStoreConfig = () => {
    const { store } = this.state;
    if (!store) return Promise.resolve(null);
    return getStoreConfig(store.location).then((r) => r.data);
  };

  handleRefreshFromCrm = async () => {
    const { store } = this.state;
    const { isSyncingFromCrm } = this.state;
    if (!store?.location || isSyncingFromCrm) return;
    this.setState({ isSyncingFromCrm: true });
    try {
      await refreshStoreFromCrm(store.location);
      try {
        const { match } = this.props;
        await this.fetchStore(match.params.id);
        toast.success("Store config refreshed from CRM");
      } catch (fetchErr) {
        toast.error(fetchErr.response?.data?.detail ?? "Failed to reload store after sync");
      }
      this.setState({ isSyncingFromCrm: false });
    } catch (err) {
      this.setState({ isSyncingFromCrm: false });
      const status = err.response?.status;
      const body = err.response?.data;
      if (status === 404) {
        toast.error("Store not found");
      } else if (status === 503) {
        const message = body?.error ?? "CRM sync unavailable";
        toast.error(message);
      } else {
        toast.error(body?.detail ?? body?.error ?? "Failed to refresh from CRM");
      }
    }
  };

  handleToggleForceActive = async () => {
    const { store, isTogglingForceActive } = this.state;
    if (!store?.location || isTogglingForceActive) return;
    const isCurrentlyForced = store.active_status === "forced_active";
    this.setState({ isTogglingForceActive: true });
    try {
      if (isCurrentlyForced) {
        await removeForceActiveStore(store.location);
      } else {
        await forceActiveStore(store.location);
      }
      try {
        const { match } = this.props;
        await this.fetchStore(match.params.id);
        toast.success(isCurrentlyForced ? "Force-active removed" : "Store forced active");
      } catch (fetchErr) {
        toast.error(fetchErr.response?.data?.detail ?? "Failed to reload store after update");
      }
    } catch (err) {
      const detail = err.response?.data?.detail ?? err.response?.data?.error;
      toast.error(detail ?? "Failed to update force-active status");
    } finally {
      this.setState({ isTogglingForceActive: false });
    }
  };

  handleAddOrUpdateVideoTune = (videoTune) => {
    const { store } = this.state;

    store.video_tune = { ...videoTune };
    this.setState({ store });
  };

  handleAddOrUpdateNotifier = (notifiers) => {
    const { store } = this.state;
    store.notifiers = { ...notifiers };
    this.setState({ store });
  };

  toggleTab = (tabIndex) => {
    const { activeTabIndex } = this.state;
    if (activeTabIndex !== tabIndex) {
      this.setState({
        activeTabIndex: tabIndex,
      });
    }
  };

  fetchServersContainersStatus = async (location, epoch) => {
    try {
      const serversStatus = await getStoreContainersStatus(location);
      if (this.isStaleFetch(epoch)) return;
      this.setState({ servers: serversStatus });
    } catch (ex) {
      if (this.isStaleFetch(epoch)) return;
      if (ex.response && ex.response.status === 404) {
        if (ex.response.data && ex.response.data.detail) {
          toast.error(ex.response.data.detail);
        } else {
          toast.error("Failed to load machine services status");
        }
      } else if (ex.response && ex.response.status === 400) {
        Object.keys(ex.response.data).forEach(function (key) {
          const message = `${key}: ${ex.response.data[key][0]}`;
          toast.error(message);
        });
      } else if (ex.response && ex.response.status === 401) {
        toast.error(ex.response.data.error);
      } else {
        toast.error("Failed to load machine services status");
      }
    }
  };

  fetchPaymentStatus = async (location, epoch) => {
    try {
      const { data: paymentStatus } = await getStorePaymentStatus(location);
      if (this.isStaleFetch(epoch)) return;
      this.setState({ paymentStatus });
    } catch {
      if (this.isStaleFetch(epoch)) return;
      this.setState({ paymentStatus: { is_unknown: true } });
    }
  };

  // The header KPIs are shown in both UI versions, so they read the camera model
  // first and only fall back to the legacy recorder tree when the store has no
  // cameras yet — otherwise a camera-only store would display zeros.
  computeStreamKpis = async (storeId, store) => {
    try {
      const tree = await getStoreTree(storeId);
      if (tree.some((device) => (device.cameras || []).length > 0)) {
        return computeStreamKpisFromCameraTree(tree);
      }
    } catch {
      // Fall through to the legacy tree carried by the store payload.
    }
    return computeStreamKpisFromLegacyStore(store);
  };

  // A store load spans several awaits, and componentDidUpdate re-enters it on every
  // route change, so a slow earlier load could otherwise land after a newer one and
  // repaint the page with the store the user has already navigated away from. Every
  // state write past an await belongs to the epoch that started it.
  isStaleFetch = (epoch) => epoch !== this.storeFetchEpoch;

  fetchStore = async (storeId) => {
    this.storeFetchEpoch += 1;
    const epoch = this.storeFetchEpoch;
    try {
      const { data: store } = await getStore(storeId);
      const { analyzedStreams, analyzedFisheyes, unactiveStreams } = await this.computeStreamKpis(storeId, store);
      if (this.isStaleFetch(epoch)) return;
      store.analyzed_streams = analyzedStreams;
      store.analyzed_fisheyes = analyzedFisheyes;
      store.unactive_streams = unactiveStreams;
      this.setState({ store });
      setDocumentTitle(store.location);
      if (store.in_furious) {
        await this.fetchPaymentStatus(store.location, epoch);
      }
      await this.fetchServersContainersStatus(store.location, epoch);
    } catch (ex) {
      if (this.isStaleFetch(epoch)) return;
      if (ex.response && ex.response.status === 404) {
        if (ex.response.data && ex.response.data.detail) {
          toast.error(ex.response.data.detail);
        } else {
          toast.error("Store not found");
        }
      } else if (ex.response && ex.response.status === 400) {
        Object.keys(ex.response.data).forEach(function (key) {
          const message = `${key}: ${ex.response.data[key][0]}`;
          toast.error(message);
        });
      } else if (ex.response && ex.response.status === 401) {
        toast.error(ex.response.data.error);
      } else {
        toast.error("Unhandled error");
      }
    }
  };

  render() {
    const {
      store,
      paymentStatus,
      newStore,
      deleteMode,
      restartMode,
      activeTabIndex,
      servers,
      isConfigModalOpen,
      isSyncingFromCrm,
      isTogglingForceActive,
    } = this.state;
    const canViewConfig = userHasAnyRole(configViewerRoles);
    const shouldDisplayLanguageForm =
      store &&
      Object.prototype.hasOwnProperty.call(store, "country") &&
      Object.prototype.hasOwnProperty.call(store, "currency") &&
      Object.prototype.hasOwnProperty.call(store, "language");
    const shouldDisplayVideoTuneForm = store && Object.prototype.hasOwnProperty.call(store, "video_tune");
    const shouldDisplayVtIpForm = store && Object.prototype.hasOwnProperty.call(store, "vt_ips");
    const shouldDisplayNotifiersForm = store && Object.prototype.hasOwnProperty.call(store, "notifiers");
    const shouldDisplayOpeningHoursForm = store && Object.prototype.hasOwnProperty.call(store, "opening_hours");
    const shouldDisplayIncidentsForm =
      store &&
      (userHasRole(csmRole) ||
        userHasRole(csmMaintenanceRole) ||
        userHasRole(qualityRole) ||
        userHasRole(adminEditorRole)) &&
      store.in_furious;
    const shouldDisplayInstallationWizard = false;
    // The Cameras tab reads the camera model straight from the store id, so it must not
    // be gated on the legacy `video_recorders` payload key: a store that no longer carries
    // that key still has a camera tree to show.
    const showCameraTab = !!store;
    // A stale `?tab=1` deep link lands on Cameras; on a store without it, fall back to
    // the store config rather than an empty pane.
    const activeTab = activeTabIndex === 9 && !showCameraTab ? 0 : activeTabIndex;
    let className = "store-config";
    if (deleteMode) className += " danger";
    if (restartMode) className += " primary";

    return (
      <div className="content">
        <Row>
          <Col md="12">
            <Card className={className}>
              <CardHeader className="mb-2">
                <h5 className="card-category">Store configuration</h5>
                <CardTitle tag="h2" className="flex justify-between">
                  <div className="flex items-center gap-2">
                    <div className="">{store ? store.location : newStore ? "Creating new store" : "Loading"}</div>
                    {store && (
                      <>
                        <Badge
                          color={
                            store.active_status === "active"
                              ? "success"
                              : store.active_status === "forced_active"
                              ? "info"
                              : store.active_status === "inactive"
                              ? "danger"
                              : "secondary"
                          }
                          className="text-sm px-3 py-1"
                        >
                          <i
                            className={`fas ${
                              store.active_status === "active"
                                ? "fa-check-circle"
                                : store.active_status === "forced_active"
                                ? "fa-lock-open"
                                : store.active_status === "inactive"
                                ? "fa-times-circle"
                                : "fa-question-circle"
                            } mr-1`}
                          />
                          {store.active_status === "active"
                            ? "Active (CRM)"
                            : store.active_status === "forced_active"
                            ? "Forced Active"
                            : store.active_status === "inactive"
                            ? "Inactive (CRM)"
                            : "No CRM Data"}
                        </Badge>
                        {(paymentStatus?.is_restricted || paymentStatus?.is_unknown) && (
                          <>
                            <Badge color="danger" className="text-sm px-3 py-1" id="payment-status-badge">
                              {paymentStatus.is_unknown ? "Payment status unknown" : "Payment restricted"}
                            </Badge>
                            <UncontrolledTooltip target="payment-status-badge">
                              {paymentStatus.is_unknown
                                ? "Furious unreachable"
                                : `Payment status: ${paymentStatus.payment_status}, exempt from restrictions: ${
                                    paymentStatus.payment_restrictions_exempt ? "yes" : "no"
                                  }`}
                            </UncontrolledTooltip>
                          </>
                        )}
                        <DisplayWhen condition={userHasRole(editorRole) || userHasRole(adminEditorRole)}>
                          <Button
                            color="secondary"
                            size="sm"
                            outline
                            className="py-1 px-2"
                            onClick={this.handleRefreshFromCrm}
                            disabled={isSyncingFromCrm}
                            title="Refresh from CRM"
                          >
                            {isSyncingFromCrm ? (
                              <Spinner size="sm" className="mr-0" />
                            ) : (
                              <i className="fas fa-sync-alt" />
                            )}
                          </Button>
                          <Button
                            color={store.active_status === "forced_active" ? "danger" : "info"}
                            size="sm"
                            outline
                            className="py-1 px-2"
                            onClick={this.handleToggleForceActive}
                            disabled={isTogglingForceActive || store.active_status === "active"}
                            title={
                              store.active_status === "forced_active" ? "Remove forced activation" : "Force activate"
                            }
                          >
                            {isTogglingForceActive ? (
                              <Spinner size="sm" className="mr-0" />
                            ) : (
                              <i
                                className={`fas ${
                                  store.active_status === "forced_active" ? "fa-lock" : "fa-lock-open"
                                }`}
                              />
                            )}
                          </Button>
                        </DisplayWhen>
                      </>
                    )}
                  </div>
                  <div>
                    {store && (
                      <Badge
                        color={store.analyzed_streams + store.analyzed_fisheyes === 0 ? "danger" : "dark"}
                        className="float-right mr-2"
                      >
                        <i className="fas fa-video mr-1" />
                        {store.analyzed_streams}
                        <i className="fas fa-eye mr-1 ml-2" />
                        {store.analyzed_fisheyes}
                        {store.analyzed_streams + store.analyzed_fisheyes > 1
                          ? " Analyzed Streams"
                          : " Analyzed Stream"}
                      </Badge>
                    )}
                    {store && (
                      <Badge color="dark" className="text-sm float-right mr-2">
                        <i className="fas fa-video-slash mr-2" />
                        {store.unactive_streams}
                      </Badge>
                    )}
                  </div>
                </CardTitle>
                <div className="flex">
                  {store && (
                    <div className="flex align-middle items-center gap-2 py-2">
                      <LinkButton href={`/screenshots/${store.location}`} label="Screenshots" />
                      <LinkButton
                        href={`/installations/${store.location}?store_name=${encodeURIComponent(store.name)}`}
                        label="Installation"
                        tooltip="View installation"
                      />
                      <DisplayWhen condition={userHasAnyRole(dashcamRoles)}>
                        <LinkButton href={`/capabilities/${store.location}`} label="Dashcam" />
                      </DisplayWhen>
                      <LinkButton
                        href={`${process.env.REACT_APP_ALERTSBOT_URL}/telegram/store_selection/?location=${store.location}&telegram_group_id=${store.telegram_group_id}&manager_store_id=${store.id}`}
                        label="AlertsBot"
                      />
                      <a
                        className="no-underline hover:no-underline"
                        href={`${process.env.REACT_APP_APPTUNE_URL}/stores/${store.location}`}
                      >
                        <button
                          type="button"
                          className="flex bg-neutral-900 hover:bg-neutral-800 font-semibold p-2 rounded-md items-center align-middle"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="h-6 w-6 text-gray-100"
                            aria-hidden="true"
                          >
                            <line x1="4" y1="6" x2="13" y2="6" />
                            <line x1="17" y1="6" x2="20" y2="6" />
                            <line x1="4" y1="12" x2="7" y2="12" />
                            <line x1="11" y1="12" x2="20" y2="12" />
                            <line x1="4" y1="18" x2="15" y2="18" />
                            <line x1="19" y1="18" x2="20" y2="18" />
                            <circle cx="15" cy="6" r="2" />
                            <circle cx="9" cy="12" r="2" />
                            <circle cx="17" cy="18" r="2" />
                          </svg>
                          <span className="text-base text-gray-100 px-2 ">AppTune</span>
                        </button>
                      </a>
                      <DisplayWhen condition={canViewConfig}>
                        <ActionButton label="View config" onClick={this.toggleConfigModal} />
                      </DisplayWhen>
                    </div>
                  )}
                </div>
              </CardHeader>
              {store && canViewConfig && (
                <ConfigViewerModal
                  isOpen={isConfigModalOpen}
                  toggle={this.toggleConfigModal}
                  title={`Store config: ${store.location}`}
                  resourceId={store.location}
                  fetch={this.handleFetchStoreConfig}
                  requestInfo={{
                    url: `/config-center/store-configs/${encodeURIComponent(store.location)}/`,
                  }}
                />
              )}
              <CardBody>
                {store && (
                  <React.Fragment>
                    <Nav tabs>
                      <NavItem role="button">
                        <NavLink
                          className={activeTab === 0 ? "active" : ""}
                          onClick={() => {
                            this.toggleTab(0);
                          }}
                        >
                          <p>Store Config</p>
                        </NavLink>
                      </NavItem>
                      {/* Tab ids are stable: `?tab=<n>` deep links are read straight off the URL. */}
                      <DisplayWhen condition={showCameraTab}>
                        <NavItem role="button">
                          <NavLink
                            className={activeTab === 9 ? "active" : ""}
                            onClick={() => {
                              this.toggleTab(9);
                            }}
                          >
                            <p>Cameras</p>
                          </NavLink>
                        </NavItem>
                      </DisplayWhen>
                      <DisplayWhen condition={shouldDisplayOpeningHoursForm}>
                        <NavItem role="button">
                          <NavLink
                            className={activeTab === 2 ? "active" : ""}
                            onClick={() => {
                              this.toggleTab(2);
                            }}
                          >
                            <p>Opening Hours</p>
                          </NavLink>
                        </NavItem>
                      </DisplayWhen>
                      <DisplayWhen condition={shouldDisplayVideoTuneForm}>
                        <NavItem role="button">
                          <NavLink
                            className={activeTab === 3 ? "active" : ""}
                            onClick={() => {
                              this.toggleTab(3);
                            }}
                          >
                            <p>Video Tune</p>
                          </NavLink>
                        </NavItem>
                      </DisplayWhen>
                      <DisplayWhen condition={shouldDisplayLanguageForm}>
                        <NavItem role="button">
                          <NavLink
                            className={activeTab === 4 ? "active" : ""}
                            onClick={() => {
                              this.toggleTab(4);
                            }}
                          >
                            <p>Language & Region</p>
                          </NavLink>
                        </NavItem>
                      </DisplayWhen>
                      <DisplayWhen condition={shouldDisplayNotifiersForm}>
                        <NavItem role="button">
                          <NavLink
                            className={activeTab === 5 ? "active" : ""}
                            onClick={() => {
                              this.toggleTab(5);
                            }}
                          >
                            <p>Alert Notifiers</p>
                          </NavLink>
                        </NavItem>
                      </DisplayWhen>
                      <DisplayWhen condition={shouldDisplayVtIpForm}>
                        <NavItem role="button">
                          <NavLink
                            className={activeTab === 6 ? "active" : ""}
                            onClick={() => {
                              this.toggleTab(6);
                            }}
                          >
                            <p>Anaveo VT IPs</p>
                          </NavLink>
                        </NavItem>
                      </DisplayWhen>
                      <NavItem role="button">
                        {!servers.status && (
                          <NavLink
                            className={activeTab === 7 ? "active" : ""}
                            onClick={() => {
                              this.toggleTab(7);
                            }}
                          >
                            <p>
                              <Spinner color="primary" size="sm" className="mr-1" />
                              Servers
                            </p>
                          </NavLink>
                        )}
                        {servers.status && (
                          <NavLink
                            className={activeTab === 7 ? "active" : ""}
                            onClick={() => {
                              this.toggleTab(7);
                            }}
                          >
                            <p>Servers</p>
                          </NavLink>
                        )}
                      </NavItem>
                      <DisplayWhen condition={shouldDisplayIncidentsForm}>
                        <NavItem role="button">
                          <NavLink
                            className={activeTab === 8 ? "active" : ""}
                            onClick={() => {
                              this.toggleTab(8);
                            }}
                          >
                            <p>Incidents</p>
                          </NavLink>
                        </NavItem>
                      </DisplayWhen>
                      <DisplayWhen condition={shouldDisplayInstallationWizard}>
                        <NavItem role="button">
                          <NavLink
                            className={activeTab === 10 ? "active" : ""}
                            onClick={() => {
                              this.toggleTab(10);
                            }}
                          >
                            <p>Installation Wizard</p>
                          </NavLink>
                        </NavItem>
                      </DisplayWhen>
                    </Nav>
                    <TabContent activeTab={activeTab}>
                      <TabPane tabId={0}>
                        <StoreConfigForm
                          store={store}
                          newStore={false}
                          toggleDeleteMode={this.toggleDeleteMode}
                          toggleRestartMode={this.toggleRestartMode}
                          deleteMode={deleteMode}
                          restartMode={restartMode}
                          fetchStore={this.fetchStore}
                          onSimulationChange={this.handleSimulationChange}
                        />
                      </TabPane>

                      <DisplayWhen condition={shouldDisplayOpeningHoursForm}>
                        <TabPane tabId={2}>
                          <OpeningHoursList
                            openingHours={store.opening_hours}
                            storeId={store.id}
                            storeTimezoneId={store.timezone}
                          />
                        </TabPane>
                      </DisplayWhen>

                      <DisplayWhen condition={shouldDisplayVideoTuneForm}>
                        <TabPane tabId={3}>
                          <VideoTuneForm
                            videoTune={store.video_tune}
                            store={store}
                            onSaveVideoTune={this.handleAddOrUpdateVideoTune}
                          />
                        </TabPane>
                      </DisplayWhen>

                      <DisplayWhen condition={shouldDisplayLanguageForm}>
                        <TabPane tabId={4}>
                          <LanguageRegionForm store={store} />
                        </TabPane>
                      </DisplayWhen>

                      <DisplayWhen condition={shouldDisplayNotifiersForm}>
                        <TabPane tabId={5}>
                          <NotifierForm
                            notifiers={store.notifiers}
                            store={store}
                            onSaveNotifier={this.handleAddOrUpdateNotifier}
                          />
                        </TabPane>
                      </DisplayWhen>

                      <DisplayWhen condition={shouldDisplayVtIpForm}>
                        <TabPane tabId={6}>
                          <VtIpList vtIps={store.vt_ips} storeId={store.id} store={store} />
                        </TabPane>
                      </DisplayWhen>

                      {!servers.status && (
                        <TabPane tabId={7}>
                          <Spinner color="primary" />
                        </TabPane>
                      )}
                      {servers.status && (
                        <TabPane tabId={7}>
                          <ServerServicesList servers={servers} store={store} />
                        </TabPane>
                      )}
                      <DisplayWhen condition={shouldDisplayIncidentsForm}>
                        <TabPane tabId={8}>
                          <IncidentList store={store} />
                        </TabPane>
                      </DisplayWhen>
                      <DisplayWhen condition={shouldDisplayInstallationWizard}>
                        <TabPane tabId={10}>
                          <InstallationWizard store={store} />
                        </TabPane>
                      </DisplayWhen>
                      <DisplayWhen condition={showCameraTab}>
                        <TabPane tabId={9}>
                          {activeTab === 9 && <TreePrototype store={store} servers={servers} />}
                        </TabPane>
                      </DisplayWhen>
                    </TabContent>
                  </React.Fragment>
                )}
                {!store && !newStore && <Spinner color="primary" />}
                <DisplayWhen condition={newStore && this.isCreationRole()}>
                  <StoreConfigForm newStore />
                </DisplayWhen>
              </CardBody>
              <CardFooter />
            </Card>
          </Col>
        </Row>
      </div>
    );
  }
}

export default Sentry.withProfiler(StoreDetailView);
